import Rete from 'rete';
import {v4 as uuid} from "uuid";
import {
    LabelAddButtonControl
} from "../controls";
import {ActionInput} from "../inputs";
import {tempActionSocket} from "../sockets";
import {SequenceComponentHelper} from "../extensions/SequenceComponentHelper";
import {
    TextAnswer,
    TextQuestion,
    Video,
    VoiceAnswer,
    Image,
    Audio,
    TextSlide,
    Info
} from "./nested_sequence_components";

export class SequenceComponent extends Rete.Component {

    static get COMPONENT_NAME() { return "Sequence"; };

    // todo: move key to sequence helper?
    // TODO: get names from static global const variable? instead of hardcoded "string"?
    static get SEQUENCE_COMPONENT_KEY() { return "SequenceComponent"; };
    static get VIDEO_CHILD_COMPONENT_KEY() { return "VideoComponent"; };
    static get VIDEO_WITH_OUTPUT_CHILD_COMPONENT_KEY() { return "VideoWithOutputComponent"; };
    static get TEXT_QUESTION_CHILD_COMPONENT_KEY() { return "TextQuestionComponent"; };
    static get VOICE_ANSWER_CHILD_COMPONENT_KEY() { return "VoiceAnswerComponent"; };
    static get VOICE_ANSWER_TIME_LIMIT_COMPONENT_KEY() { return "VoiceAnswerTimeLimitComponent"; };
    static get DEFAULT_VOICE_ANSWER_CHILD_COMPONENT_KEY() { return "DefaultVoiceAnswerComponent"; };
    static get TEXT_ANSWER_CHILD_COMPONENT_KEY() { return "TextAnswerComponent"; };
    static get TEXT_ANSWER_TIME_LIMIT_COMPONENT_KEY() { return "TextAnswerTimeLimitComponent"; };
    static get IMAGE_CHILD_COMPONENT_KEY() { return "ImageComponent"; };
    static get AUDIO_CHILD_COMPONENT_KEY() { return "AudioComponent"; };
    static get TEXT_SLIDE_CHILD_COMPONENT_KEY() { return "TextSlideComponent"; };
    static get NESTED_NODE_TITLE_KEY() { return "nestedNodeTitle"; };
    static get INFO_CHILD_COMPONENT_KEY() { return "InfoComponent"; };

    static get SEQUENCE_ACTION_INPUT_KEY() { return "input"; };

    static get VIDEO_REMOVE_BUTTON_KEY() { return "removeButton"; };
    static get VIDEO_FILE_KEY() { return "videoFile";};
    static get VIDEO_MAPPING_KEY() { return "videoMapping";};
    static get STEREO_MODE_KEY() { return "stereoMode";};
    static get START_FRAME_KEY() { return "startFrame";};
    static get STOP_FRAME_KEY() { return "stopFrame";};
    static get VIDEO_OUTPUT_KEY() { return "output"; };

    static get VIDEO_WITH_OUTPUT_REMOVE_BUTTON_KEY() { return "removeButton"; };
    static get VIDEO_WITH_OUTPUT_FILE_KEY() { return "videoFile";};
    static get VIDEO_WITH_OUTPUT_MAPPING_KEY() { return "videoMapping";};
    static get VIDEO_WITH_OUTPUT_STEREO_MODE_KEY() { return "stereoMode";};
    static get VIDEO_WITH_OUTPUT_START_FRAME_KEY() { return "startFrame";};
    static get VIDEO_WITH_OUTPUT_STOP_FRAME_KEY() { return "stopFrame";};
    static get VIDEO_WITH_OUTPUT_OUTPUT_KEY() { return "output"; };

    static get TEXT_QUESTION_REMOVE_BUTTON_KEY() { return "removeButton"; };
    static get TEXT_QUESTION_QUESTION_KEY() { return "question";};

    static get VOICE_ANSWER_REMOVE_BUTTON_KEY() { return "removeButton"; };
    static get VOICE_ANSWER_KEYWORDS_KEY() { return "keywords"; };
    static get VOICE_ANSWER_SCORE_KEY() { return "score"; }; // TODO: score and defaultScore? would be recognized as same in string.includes() ???
    static get VOICE_ANSWER_TIME_LIMIT_KEY() { return "timeLimit"; };
    static get DEFAULT_VOICE_ANSWER_SCORE_KEY() { return "defaultScore"; };
    static get DEFAULT_VOICE_ANSWER_NODE_SEARCH_KEY() { return "nodeSearch"; };
    static get VOICE_ANSWER_OUTPUT_KEY() { return "output"; };

    static get TEXT_ANSWER_REMOVE_BUTTON_KEY() { return "removeButton"; };
    static get TEXT_ANSWER_ANSWER_KEY() { return "answer"; };
    static get TEXT_ANSWER_SCORE_KEY() { return "score"; }; // TODO: score and defaultScore? would be recognized as same in string.includes() ???
    static get TEXT_ANSWER_ORDER_KEY() { return "order"; };
    static get TEXT_ANSWER_TIME_LIMIT_KEY() { return "timeLimit"; };
    static get TEXT_ANSWER_OUTPUT_KEY() { return "output"; };

    static get IMAGE_FILE_KEY() { return "imageFile";};
    static get IMAGE_DURATION_KEY() { return "duration";};
    static get IMAGE_REMOVE_BUTTON_KEY() { return "removeButton"; };
    static get IMAGE_OUTPUT_KEY() { return "output"; };

    static get TEXT_SLIDE_HEADER_KEY() { return "header";};
    static get TEXT_SLIDE_TEXT_KEY() { return "text";};
    static get TEXT_SLIDE_DURATION_KEY() { return "duration";};
    static get TEXT_SLIDE_REMOVE_BUTTON_KEY() { return "removeButton"; };
    static get TEXT_SLIDE_OUTPUT_KEY() { return "output"; };

    static get AUDIO_FILE_KEY() { return "audioFile";};
    static get AUDIO_REMOVE_BUTTON_KEY() { return "removeButton"; };

    static get INFO_ICON_FILE_KEY() { return "iconFile";};
    static get INFO_TEXT_KEY() { return "text";};
    static get INFO_REMOVE_BUTTON_KEY() { return "removeButton"; };
    static get INFO_ORDER_KEY() { return "order"; };


    constructor() {
        super(SequenceComponent.COMPONENT_NAME);
        this.props = {}
    }

    builder(node) {
        ///////////////////////////////////////
        /// --- Sequence Component (parent) ---
        ///////////////////////////////////////
        if (node.isCloned) {
            node.data = SequenceComponentHelper.cloneData(node.data);
        }

        if (node.componentId === undefined) {
            node.componentId = uuid();
        }

        if (node.customName === undefined) {
            node.customName = "";
        }

        const sequenceComponentUUID = node.componentId;

        const inAction = new ActionInput(
            `${sequenceComponentUUID}_${SequenceComponent.SEQUENCE_COMPONENT_KEY}_${SequenceComponent.SEQUENCE_ACTION_INPUT_KEY}`,
            "action",
            tempActionSocket,
            true);



        ////////////////////////////////////////
        /// --- Nested Components (children) ---
        ///////////////////////////////////////
        node.nestedComponents = {};
        const videoUUID = uuid();
        const addVideoButtonControl = new LabelAddButtonControl(
            this.editor,
            `${videoUUID}_${SequenceComponent.VIDEO_CHILD_COMPONENT_KEY}_${SequenceComponent.NESTED_NODE_TITLE_KEY}`,
            node,
            false,
            "Video: ",
            () => node.nestedComponents.video.addVideo(node),
            true);
        node.addVideoComponent = addVideoButtonControl;
        node.nestedComponents.video = new Video(this,
            () => this.onVideoAdded(node),
            () => this.onVideoRemoved(node));

        const textQuestionUUID = uuid();
        const textQuestionButtonControl = new LabelAddButtonControl(
            this.editor,
            `${textQuestionUUID}_${SequenceComponent.TEXT_QUESTION_CHILD_COMPONENT_KEY}_${SequenceComponent.NESTED_NODE_TITLE_KEY}`,
            node,
            false,
            "Text Question: ",
            () => node.nestedComponents.textQuestion.addTextQuestion(node),
            true);
        node.addTextQuestionComponent = textQuestionButtonControl;
        node.nestedComponents.textQuestion = new TextQuestion(this,
            () => this.onTextQuestionAdded(node),
            () => this.onTextQuestionRemoved(node));

        const voiceAnswerUUID = uuid();
        const addVoiceAnswerButtonControl = new LabelAddButtonControl(
            this.editor,
            `${voiceAnswerUUID}_${SequenceComponent.VOICE_ANSWER_CHILD_COMPONENT_KEY}_${SequenceComponent.NESTED_NODE_TITLE_KEY}`,
            node,
            false,
            "Voice Answer: ",
            () => node.nestedComponents.voiceAnswer.addVoiceAnswer(node),
            true);
        node.addVoiceAnswerComponent = addVoiceAnswerButtonControl;
        node.nestedComponents.voiceAnswer = new VoiceAnswer(this,
            () => this.onVoiceAnswerAdded(node),
            () => this.onVoiceAnswerRemoved(node));

        const textAnswerUUID = uuid();
        const addTextAnswerButtonControl = new LabelAddButtonControl(
            this.editor,
            `${textAnswerUUID}_${SequenceComponent.TEXT_ANSWER_CHILD_COMPONENT_KEY}_${SequenceComponent.NESTED_NODE_TITLE_KEY}`,
            node,
            false,
            "Text Answer: ",
            () => node.nestedComponents.textAnswer.addTextAnswer(node),
            true);
        node.addTextAnswerComponent = addTextAnswerButtonControl;
        node.nestedComponents.textAnswer = new TextAnswer(this,
            () => this.onTextAnswerAdded(node),
            () => this.onTextAnswerRemoved(node));

        const imageUUID = uuid();
        const imageButtonControl = new LabelAddButtonControl(
            this.editor,
            `${imageUUID}_${SequenceComponent.IMAGE_CHILD_COMPONENT_KEY}_${SequenceComponent.NESTED_NODE_TITLE_KEY}`,
            node,
            false,
            "Image: ",
            () => node.nestedComponents.image.addImage(node),
            true);
        node.addImageComponent = imageButtonControl;
        node.nestedComponents.image = new Image(this,
            () => this.onImageAdded(node),
            () => this.onImageRemoved(node));

        const textSlideUUID = uuid();
        const textSlideButtonControl = new LabelAddButtonControl(
            this.editor,
            `${textSlideUUID}_${SequenceComponent.TEXT_SLIDE_CHILD_COMPONENT_KEY}_${SequenceComponent.NESTED_NODE_TITLE_KEY}`,
            node,
            false,
            "Text Slide: ",
            () => node.nestedComponents.textSlide.addTextSlide(node),
            true);
        node.addTextSlideComponent = textSlideButtonControl;
        node.nestedComponents.textSlide = new TextSlide(this,
            () => this.onTextSlideAdded(node),
            () => this.onTextSlideRemoved(node));

        const audioUUID = uuid();
        const audioButtonControl = new LabelAddButtonControl(
            this.editor,
            `${audioUUID}_${SequenceComponent.AUDIO_CHILD_COMPONENT_KEY}_${SequenceComponent.NESTED_NODE_TITLE_KEY}`,
            node,
            false,
            "Audio: ",
            () => node.nestedComponents.audio.addAudio(node),
            true);
        node.addAudioComponent = audioButtonControl;
        node.nestedComponents.audio = new Audio(this,
            () => this.onAudioAdded(node),
            () => this.onAudioRemoved(node));

        const infoUUID = uuid();
        const infoButtonControl = new LabelAddButtonControl(
            this.editor,
            `${infoUUID}_${SequenceComponent.INFO_CHILD_COMPONENT_KEY}_${SequenceComponent.NESTED_NODE_TITLE_KEY}`,
            node,
            false,
            "Info: ",
            () => node.nestedComponents.info.addInfo(node),
            true);
        node.addInfoComponent = infoButtonControl;
        node.nestedComponents.info = new Info(this,
            () => this.onInfoAdded(node),
            () => this.onInfoRemoved(node));

        const {videoId, videoWithOutputId, textQuestionId, voiceAnswerIds, voiceAnswerTimeLimitId, textAnswerIds,  defaultVoiceAnswerId, textAnswerTimeLimitId, imageId, textSlideId, audioId, infoIds} = SequenceComponentHelper.getDataIds(node.data);

        // 'DefaultVoiceAnswer' must be loaded before 'VoiceAnswer'.
        // If default won't exist during VoiceAnswer creation, it will be added and there will be 2 or more Default
        if (defaultVoiceAnswerId !== null) {
            node.nestedComponents.voiceAnswer.addDefaultVoiceAnswer(node, defaultVoiceAnswerId);
            node.defaultVoiceAnswerId = defaultVoiceAnswerId;
        }

        // 'VoiceAnswerTimeLimit' must be loaded before 'VoiceAnswer'.
        // If time limit won't exist during VoiceAnswer creation, it will be added and there will be 2 or more time limit components
        if (voiceAnswerTimeLimitId !== null) {
            node.nestedComponents.voiceAnswer.addVoiceAnswerTimeLimit(node, voiceAnswerTimeLimitId);
            node.voiceAnswerTimeLimitId = voiceAnswerTimeLimitId;
        }

        // 'TextAnswerTimeLimit' must be loaded before 'TextAnswer'.
        // If time limit won't exist during TextAnswer creation, it will be added and there will be 2 or more time limit components
        if (textAnswerTimeLimitId !== null) {
            node.nestedComponents.textAnswer.addTextAnswerTimeLimit(node, textAnswerTimeLimitId);
            node.textAnswerTimeLimitId = textAnswerTimeLimitId;
        }

        if (videoId !== null) {
            node.nestedComponents.video.addVideo(node, videoId);
        }

        // TODO: remove in future, and other VideoWithOutput leftovers
        // VideoWithOutput was removed, but backwards compatibility is required. Replacing it by Video.
        if (videoId == null && videoWithOutputId !== null) {
            node.nestedComponents.video.addVideo(node, videoWithOutputId);
        }

        if (textQuestionId !== null) {
            node.nestedComponents.textQuestion.addTextQuestion(node, textQuestionId);
        }

        for(let i = 0; i < voiceAnswerIds.length; i++) {
            node.nestedComponents.voiceAnswer.addVoiceAnswer(node, voiceAnswerIds[i]);
        }

        for(let i = 0; i < textAnswerIds.length; i++) {
            node.nestedComponents.textAnswer.addTextAnswer(node, textAnswerIds[i]);
        }

        if (imageId !== null) {
            node.nestedComponents.image.addImage(node, imageId);
        }

        if (textSlideId !== null) {
            node.nestedComponents.textSlide.addTextSlide(node, textSlideId);
        }

        if (audioId !== null) {
            node.nestedComponents.audio.addAudio(node, audioId);
        }

        for(let i = 0; i < infoIds.length; i++) {
            node.nestedComponents.info.addInfo(node, infoIds[i]);
        }

        return node
            .addInput(inAction)
            .addControl(addVideoButtonControl)
            .addControl(textQuestionButtonControl)
            .addControl(addVoiceAnswerButtonControl)
            .addControl(addTextAnswerButtonControl)
            .addControl(imageButtonControl)
            .addControl(textSlideButtonControl)
            .addControl(audioButtonControl)
            .addControl(infoButtonControl);
    }

    // could be async
    worker(node, inputs, outputs) {

    }

    onVideoAdded(node) {
        node.addVideoComponent.setComponentIsAdded(true);
        node.addVideoComponent.setAddButtonActive(false);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    onVideoRemoved(node) {
        node.addVideoComponent.setComponentIsAdded(false);
        node.addVideoComponent.setAddButtonActive(true);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    onTextQuestionAdded(node) {
        node.addTextQuestionComponent.setComponentIsAdded(true);
        node.addTextQuestionComponent.setAddButtonActive(false);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    onTextQuestionRemoved(node) {
        node.addTextQuestionComponent.setComponentIsAdded(false);
        node.addTextQuestionComponent.setAddButtonActive(true);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    onTextAnswerAdded(node) {
        node.nestedComponents.video.handleAnswerAdded(node);
        node.nestedComponents.image.handleAnswerAdded(node);
        node.nestedComponents.textSlide.handleAnswerAdded(node);
        node.addTextAnswerComponent.setComponentIsAdded(true);
        node.addTextAnswerComponent.setAddButtonActive(false);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    onTextAnswerRemoved(node) {
        node.nestedComponents.video.handleAnswerRemoved(node);
        node.nestedComponents.image.handleAnswerRemoved(node);
        node.nestedComponents.textSlide.handleAnswerRemoved(node);
        node.addTextAnswerComponent.setComponentIsAdded(false);
        node.addTextAnswerComponent.setAddButtonActive(true);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    onVoiceAnswerAdded(node) {
        node.nestedComponents.video.handleAnswerAdded(node);
        node.nestedComponents.image.handleAnswerAdded(node);
        node.nestedComponents.textSlide.handleAnswerAdded(node);
        node.addVoiceAnswerComponent.setComponentIsAdded(true);
        node.addVoiceAnswerComponent.setAddButtonActive(false);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    onVoiceAnswerRemoved(node) {
        node.nestedComponents.video.handleAnswerRemoved(node);
        node.nestedComponents.image.handleAnswerRemoved(node);
        node.nestedComponents.textSlide.handleAnswerRemoved(node);
        node.addVoiceAnswerComponent.setComponentIsAdded(false);
        node.addVoiceAnswerComponent.setAddButtonActive(true);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    onImageAdded(node) {
        node.addImageComponent.setComponentIsAdded(true);
        node.addImageComponent.setAddButtonActive(false);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    onImageRemoved(node) {
        node.addImageComponent.setComponentIsAdded(false);
        node.addImageComponent.setAddButtonActive(true);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    onTextSlideAdded(node) {
        node.addTextSlideComponent.setComponentIsAdded(true);
        node.addTextSlideComponent.setAddButtonActive(false);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    onTextSlideRemoved(node) {
        node.addTextSlideComponent.setComponentIsAdded(false);
        node.addTextSlideComponent.setAddButtonActive(true);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    onAudioAdded(node) {
        node.addAudioComponent.setComponentIsAdded(true);
        node.addAudioComponent.setAddButtonActive(false);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    onAudioRemoved(node) {
        node.addAudioComponent.setComponentIsAdded(false);
        node.addAudioComponent.setAddButtonActive(true);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    onInfoAdded(node) {
        node.addInfoComponent.setComponentIsAdded(true);
        node.addInfoComponent.setAddButtonActive(false);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    onInfoRemoved(node) {
        node.addInfoComponent.setComponentIsAdded(false);
        node.addInfoComponent.setAddButtonActive(true);
        this.updateActivity(node);
        this.editor.trigger("process");
    }

    updateActivity(node) {
        node.addVideoComponent.setAddButtonActive(
            !node.nestedComponents.video.isAdded
            && !node.nestedComponents.image.isAdded
            && !node.nestedComponents.audio.isAdded
            && !node.nestedComponents.textSlide.isAdded);

        node.addTextQuestionComponent.setAddButtonActive(
            !node.nestedComponents.textQuestion.isAdded
            && !node.nestedComponents.audio.isAdded
            && !node.nestedComponents.textSlide.isAdded);

        node.addVoiceAnswerComponent.setAddButtonActive(
            !node.nestedComponents.textAnswer.isAdded
            && !node.nestedComponents.image.isAdded
            && !node.nestedComponents.audio.isAdded
            && !node.nestedComponents.textSlide.isAdded);

        node.addTextAnswerComponent.setAddButtonActive(
            !node.nestedComponents.voiceAnswer.isAdded
            && !node.nestedComponents.audio.isAdded
            && !node.nestedComponents.textSlide.isAdded);

        node.addImageComponent.setAddButtonActive(
            !node.nestedComponents.image.isAdded
            && !node.nestedComponents.video.isAdded
            && !node.nestedComponents.voiceAnswer.isAdded
            && !node.nestedComponents.textSlide.isAdded);

        node.addTextSlideComponent.setAddButtonActive(
            !node.nestedComponents.textSlide.isAdded
            && !node.nestedComponents.video.isAdded
            && !node.nestedComponents.image.isAdded
            && !node.nestedComponents.textQuestion.isAdded
            && !node.nestedComponents.textAnswer.isAdded
            && !node.nestedComponents.voiceAnswer.isAdded);

        node.addAudioComponent.setAddButtonActive(
            !node.nestedComponents.audio.isAdded
            && !node.nestedComponents.video.isAdded
            && !node.nestedComponents.textQuestion.isAdded
            && !node.nestedComponents.textAnswer.isAdded
            && !node.nestedComponents.voiceAnswer.isAdded);

        node.addInfoComponent.setAddButtonActive(true);
    }
}